import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertCircle, LogIn, Mail, Lock, KeyRound, UserCog } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// L'URL del backend arriva sempre da REACT_APP_BACKEND_URL: nessun valore
// scritto fisso, altrimenti il bundle resta legato a un ambiente.

// Tap x5 sul logo per il quick login sviluppatore: 5 tocchi entro 2s.
// L'endpoint /dev/quick-login/* esiste solo se ENABLE_DEV_QUICK_LOGIN=true sul
// backend (mai in produzione): altrove risponde 404 e qui non succede nulla,
// senza toast d'errore che riveli la funzionalità.
const QUICK_LOGIN_TAPS = 5;
const QUICK_LOGIN_WINDOW_MS = 2000;

export default function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [quickLoginUsers, setQuickLoginUsers] = useState(null); // null = dialog chiuso
  const [quickLoginBusy, setQuickLoginBusy] = useState(null); // user_id in corso di accesso
  const [tapCount, setTapCount] = useState(0); // feedback visivo dei tap sul logo
  const tapTimestamps = useRef([]);
  const tapResetTimer = useRef(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem("session_token");
        if (!token) return;
        const response = await fetch(`${API}/auth/me`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (response.ok) {
          navigate("/dashboard", { replace: true });
        } else {
          localStorage.removeItem("session_token");
        }
      } catch (error) {
        // Not authenticated, stay on login page
      }
    };
    checkAuth();

    if (location.state?.error) {
      toast.error(location.state.error);
    }

    // Il callback Google rimanda qui con ?auth_error=<motivo> quando il login
    // fallisce. Senza questo blocco l'utente tornerebbe a una pagina muta.
    const authError = new URLSearchParams(location.search).get("auth_error");
    if (authError) {
      const messaggi = {
        not_allowed: "Email non autorizzata. Contatta il Project Manager per essere aggiunto.",
        access_denied: "Accesso a Google annullato.",
        email_not_verified: "L'email del tuo account Google non risulta verificata.",
        invalid_state: "Sessione di login scaduta. Riprova.",
        state_expired: "Sessione di login scaduta. Riprova.",
        token_exchange: "Google non ha confermato l'accesso. Riprova.",
        userinfo: "Google non ha restituito i dati dell'account. Riprova.",
        network: "Errore di rete durante l'accesso a Google. Riprova.",
        missing_code: "Risposta di Google incompleta. Riprova.",
        no_email: "L'account Google non espone un indirizzo email.",
      };
      toast.error(messaggi[authError] || `Accesso non riuscito (${authError}).`);
      // Ripulisce l'URL, così un aggiornamento della pagina non ripropone l'errore
      navigate("/login", { replace: true });
    }
  }, [location.state, location.search, navigate]);

  const handleGoogleLogin = async () => {
    // Il backend costruisce l'URL di Google: client_id e redirect_uri restano
    // lato server, e lo "state" anti-CSRF viene emesso e verificato da lui.
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/google/connect`);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        toast.error(body.detail || "Login con Google non disponibile. Usa email e password.");
        return;
      }
      const { auth_url } = await response.json();
      window.location.href = auth_url;
    } catch (error) {
      console.error("[AUTH] /auth/google/connect non raggiungibile:", error);
      toast.error("Impossibile contattare il server. Usa email e password.");
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Inserisci email e password");
      return;
    }
    if (mode === "register" && password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login"
        ? { email: email.trim(), password }
        : { email: email.trim(), password, name: name.trim() || undefined };
      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = typeof data.detail === "string" ? data.detail : "Login non riuscito";
        toast.error(detail);
        return;
      }
      if (data.session_token) {
        localStorage.setItem("session_token", data.session_token);
        toast.success(mode === "login" ? "Accesso effettuato" : "Registrazione completata");
        navigate("/dashboard", { state: { user: data }, replace: true });
      } else {
        toast.error("Risposta server non valida");
      }
    } catch (err) {
      toast.error(`Errore di rete: ${err?.message || err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogoTap = async () => {
    const now = Date.now();
    tapTimestamps.current = tapTimestamps.current.filter((t) => now - t < QUICK_LOGIN_WINDOW_MS);
    tapTimestamps.current.push(now);

    setTapCount(tapTimestamps.current.length);
    clearTimeout(tapResetTimer.current);
    tapResetTimer.current = setTimeout(() => setTapCount(0), QUICK_LOGIN_WINDOW_MS);

    if (tapTimestamps.current.length < QUICK_LOGIN_TAPS) return;
    tapTimestamps.current = [];
    setTapCount(0);
    clearTimeout(tapResetTimer.current);

    try {
      const response = await fetch(`${API}/dev/quick-login/users`);
      if (!response.ok) return; // 404 in produzione/staging: nessuna traccia visibile
      const data = await response.json();
      setQuickLoginUsers(data.items || []);
    } catch {
      // Backend irraggiungibile o funzionalità assente: nessun feedback, per design.
    }
  };

  const handleQuickLogin = async (user) => {
    setQuickLoginBusy(user.user_id);
    try {
      const response = await fetch(`${API}/dev/quick-login/${user.user_id}`, { method: "POST" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.detail || "Quick login non riuscito");
        return;
      }
      localStorage.setItem("session_token", data.session_token);
      setQuickLoginUsers(null);
      toast.success(`Accesso rapido come ${user.name || user.email}`);
      navigate("/dashboard", { state: { user: data }, replace: true });
    } catch (err) {
      toast.error(`Errore di rete: ${err?.message || err}`);
    } finally {
      setQuickLoginBusy(null);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-8 flex flex-col items-start gap-3">
            <div
              className="relative w-14 h-14 rounded-2xl bg-black flex items-center justify-center p-2 cursor-pointer select-none"
              onClick={handleLogoTap}
            >
              <img src="/diecixcento-mark.png" alt="Diecixcento" className="w-full h-full object-contain" draggable={false} />
              {tapCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-indigo-600 text-white text-xs font-medium flex items-center justify-center">
                  {tapCount}
                </span>
              )}
            </div>
            <div>
              <img src="/diecixcento-logo.png" alt="diecixcento" className="h-10 w-auto object-contain mb-2" />
              <p className="text-muted-foreground">
                Gestisci le scadenze del tuo team Social Media Manager
              </p>
            </div>
          </div>

          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="font-heading text-2xl">Accedi</CardTitle>
              <CardDescription>
                Scegli il metodo di accesso che preferisci
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {location.state?.error && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{location.state.error}</span>
                </div>
              )}

              <Tabs defaultValue="password" className="w-full">
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="password" data-testid="tab-password">
                    <KeyRound className="w-4 h-4 mr-2" />
                    Email & Password
                  </TabsTrigger>
                  <TabsTrigger value="google" data-testid="tab-google">
                    <LogIn className="w-4 h-4 mr-2" />
                    Google
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="password" className="mt-4">
                  <form onSubmit={handlePasswordSubmit} className="space-y-3">
                    {mode === "register" && (
                      <div className="space-y-1.5">
                        <Label htmlFor="name">Nome</Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="Il tuo nome"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          data-testid="register-name-input"
                          autoComplete="name"
                        />
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="nome@esempio.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-9"
                          data-testid="login-email-input"
                          autoComplete="email"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type="password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-9"
                          data-testid="login-password-input"
                          autoComplete={mode === "login" ? "current-password" : "new-password"}
                          required
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      data-testid="password-submit-btn"
                      disabled={submitting}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11 text-base font-medium rounded-lg transition-all active:scale-95"
                    >
                      {submitting ? "Attendere..." : (mode === "login" ? "Accedi" : "Crea account")}
                    </Button>
                    <button
                      type="button"
                      data-testid="toggle-register-btn"
                      onClick={() => setMode(mode === "login" ? "register" : "login")}
                      className="w-full text-xs text-indigo-600 hover:text-indigo-700 underline-offset-4 hover:underline"
                    >
                      {mode === "login"
                        ? "Prima volta? Crea il tuo account (devi essere nella allowlist)"
                        : "Hai già un account? Torna al login"}
                    </button>
                  </form>
                </TabsContent>

                <TabsContent value="google" className="mt-4">
                  <Button
                    data-testid="google-login-btn"
                    onClick={handleGoogleLogin}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12 text-base font-medium rounded-lg transition-all active:scale-95"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Accedi con Google
                  </Button>
                </TabsContent>
              </Tabs>

              <p className="text-xs text-center text-muted-foreground mt-4">
                L'accesso è riservato ai membri del team autorizzati.
                <br />
                Contatta il Project Manager se non riesci ad accedere.
              </p>
            </CardContent>
          </Card>

          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p>
              <LogIn className="w-4 h-4 inline mr-1" />
              App installabile su mobile e desktop
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Hero (desktop only) */}
      <div className="hidden lg:flex w-1/2 bg-black items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/40 to-black"></div>
        <img src="/diecixcento-mark.png" alt="Diecixcento" className="relative w-72 h-72 object-contain opacity-90" />
        <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-md rounded-xl p-6 border border-white/20">
          <img src="/diecixcento-logo.png" alt="diecixcento" className="h-8 w-auto object-contain mb-3" />
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">
            Gestione Scadenze Semplificata
          </h2>
          <ul className="space-y-2 text-slate-700 text-sm">
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              Sistema semaforo per priorità visive
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              Notifiche email e push automatiche
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
              Import da Excel e generazione task automatica
            </li>
          </ul>
        </div>
      </div>

      {/* Quick login sviluppatore — solo locale, vedi handleLogoTap */}
      <Dialog open={quickLoginUsers !== null} onOpenChange={(open) => !open && setQuickLoginUsers(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Quick login (solo locale)
            </DialogTitle>
            <DialogDescription>
              Scegli un'utenza per accedere senza password.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-1">
            {(quickLoginUsers || []).map((u) => (
              <button
                key={u.user_id}
                type="button"
                disabled={quickLoginBusy !== null}
                onClick={() => handleQuickLogin(u)}
                className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-left transition-colors disabled:opacity-50"
              >
                <div>
                  <div className="font-medium text-sm">{u.name || u.email}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </div>
                <span className="text-xs uppercase tracking-wide text-indigo-600 font-medium">
                  {quickLoginBusy === u.user_id ? "..." : u.role}
                </span>
              </button>
            ))}
            {quickLoginUsers?.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Nessuna utenza attiva trovata</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
